import { useNavigate } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { adminFapiUserManagement, fapiRoleService } from '@/services/admin'
import { userManagementQueryKeys } from '@/libs/constants/queryKeys'
import { linkGenerator } from '@/libs/utils/language'
import toast from '@/libs/utils/toast'
import type { ApiInvitationRole, InvitationRolePayload } from '@/types/api/admin/roles.types'
import type { UpdateInvitationBody, UpdateUserBody } from '@/types/api/admin/user.types'

import { userDetailQueries } from '../queries'

export interface InviteUserFormValues {
  email: string
  phoneNumber: string
  language: string
  status: string
  customerNumber: string
  countryId: string
  roles: InvitationRolePayload[]
  salutation?: string
  firstName?: string
  lastName?: string
}

export interface UpdateInvitationFormValues extends InviteUserFormValues {
  invitationId: string
}

export const useInviteNewUser = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async (userData: InviteUserFormValues) =>
      adminFapiUserManagement.inviteNewUser(userData),
    onSuccess: () => {
      toast.success({
        message: 'userManagement.messages.createUserInvitationSuccess',
      })
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.lists(),
      })
      navigate(linkGenerator('/user-management'))
    },
    onError: () => {
      toast.error({
        message: 'notification.error.submit',
      })
    },
  })
}

export const useEditInvitation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      { userId, data }: { userId: string; data: UpdateInvitationFormValues },
    ) => adminFapiUserManagement.updateInvitation(userId)(data as unknown as UpdateInvitationBody),
    onSuccess: (_, { userId }) => {
      toast.success({
        message: 'userManagement.messages.editUserSuccess',
      })
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: userDetailQueries.invitation(userId).queryKey,
      })
    },
    onError: () => {
      toast.error({
        message: 'userManagement.messages.editUserError',
      })
    },
  })
}

export const useEditUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: Record<string, unknown> }) =>
      adminFapiUserManagement.editUser(userId)(data as unknown as UpdateUserBody),
    onSuccess: (_, { userId }) => {
      toast.success({
        message: 'userManagement.messages.editUserSuccess',
      })
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: userDetailQueries.detail(userId).queryKey,
      })
    },
    onError: () => {
      toast.error({
        message: 'userManagement.messages.editUserError',
      })
    },
  })
}

export const useEditUserEmail = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      userId,
      email,
      countryId,
      language,
    }: {
      userId: string
      email: string
      countryId: string
      language: string
    }) => {
      const requestBody = {
        email,
        countryId,
        language,
      }
      return adminFapiUserManagement.editUserEmailAddress(userId)(requestBody)
    },
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: userDetailQueries.detail(userId).queryKey,
      })
    },
    onError: () => {
      toast.error({
        message: 'notification.error.updateEmailAddress',
      })
    },
  })
}

export const useEditCustomerSap = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      userId,
      email,
      sapCustomerId,
      countryId,
    }: {
      userId: string
      email: string
      sapCustomerId: string
      countryId: string
    }) => {
      const requestBody = {
        countryId,
        sapCustomerId,
        email,
      }
      return adminFapiUserManagement.editCustomerSap(userId)(requestBody)
    },
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: userDetailQueries.detail(userId).queryKey,
      })
    },
    onError: () => {
      toast.error({
        message: 'notification.error.updateSapCustomerNumber',
      })
    },
  })
}

export const useAssignRolesToUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, roleIds }: { userId: string; roleIds: ApiInvitationRole[] }) =>
      fapiRoleService.assignRolesToUser({ userId, roles: roleIds }),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: userDetailQueries.detail(userId).queryKey,
      })
    },
    onError: () => {
      toast.error({
        message: 'notification.error.assignRoles',
      })
    },
  })
}

export const useDeleteUser = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async ({ userId, deleteBy }: { userId: string; deleteBy: string }) =>
      adminFapiUserManagement.deleteUserAccount(userId, deleteBy),
    onSuccess: () => {
      toast.success({
        message: 'notification.success.deleteUser',
      })
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.lists(),
      })
      navigate(linkGenerator('/user-management'))
    },
    onError: () => {
      toast.error({
        message: 'notification.error.deleteUser',
      })
    },
  })
}

export const useRevokeInvitation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      invitationId,
      reactivateRevoke,
    }: {
      invitationId: string
      reactivateRevoke: boolean
    }) => adminFapiUserManagement.revokeInvitation(invitationId, reactivateRevoke),
    onSuccess: (_, { invitationId, reactivateRevoke }) => {
      toast.success({
        message: reactivateRevoke
          ? 'notification.success.reactivateInvitation'
          : 'notification.success.revokeInvitation',
      })
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: userDetailQueries.invitation(invitationId).queryKey,
      })
    },
    onError: () => {
      toast.error({
        message: 'notification.error.revokeInvitation',
      })
    },
  })
}

export const useDeleteInvitation = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async ({ invitationId, countryId }: { invitationId: string; countryId: string }) =>
      adminFapiUserManagement.deleteInvitation(invitationId, countryId),
    onSuccess: () => {
      toast.success({
        message: 'notification.success.deleteInvitation',
      })
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.lists(),
      })
      navigate(linkGenerator('/user-management'))
    },
    onError: () => {
      toast.error({
        message: 'notification.error.deleteInvitation',
      })
    },
  })
}

export const useExportUserList = () => {
  return useMutation({
    mutationFn: async (userType: string) => adminFapiUserManagement.exportUserList(userType),
    onSuccess: (response) => {
      if (!response?.data) {
        toast.error({
          message: 'notification.error.export',
          variables: { type: 'users' },
        })
        return
      }

      const a = document.createElement('a')
      const blob = new Blob([response.data as BlobPart], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const date = new Date().toISOString().slice(0, 10)

      a.setAttribute('href', url)
      a.setAttribute('download', `export_users_${date}.csv`)
      document.body.append(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)

      toast.success({
        message: 'notification.success.export',
        variables: { type: 'users' },
      })
    },
    onError: () => {
      toast.error({
        message: 'notification.error.export',
        variables: { type: 'users' },
      })
    },
  })
}

export const useUploadInvitations = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (formData: FormData) => adminFapiUserManagement.uploadInvitations(formData),
    onSuccess: () => {
      toast.success({
        message: 'notification.success.upload',
      })
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.lists(),
      })
    },
    onError: () => {
      toast.error({
        message: 'notification.error.submit',
      })
    },
  })
}

interface EditUserCompleteData {
  firstName: string
  lastName: string
  phoneNumber: string
  language: string
  status: string
  sessionBasedShoppingCart?: boolean
  email?: string
  countryId?: string
  sapCustomerNumber?: string
  roleIds?: ApiInvitationRole[]
}

export const useEditUserComplete = () => {
  const editUserMutation = useEditUser()
  const editUserEmailMutation = useEditUserEmail()
  const editCustomerSapMutation = useEditCustomerSap()
  const assignRolesMutation = useAssignRolesToUser()
  const updateInvitationMutation = useEditInvitation()

  const isPending = editUserMutation.isPending
    || editUserEmailMutation.isPending
    || editCustomerSapMutation.isPending
    || assignRolesMutation.isPending
    || updateInvitationMutation.isPending

  const mutate = async ({
    userId,
    data,
    isInvitation,
    hasEmailChanged,
    hasSapChanged,
    hasRolesChanged,
    onSuccess,
  }: {
    userId: string
    data: EditUserCompleteData
    isInvitation: boolean
    hasEmailChanged: boolean
    hasSapChanged: boolean
    hasRolesChanged: boolean
  onSuccess?: () => void
  }) => {
    try {
      if (isInvitation) {
        const { roleIds, sapCustomerNumber, ...invitationData } = data
        // API field name differs: from uses `sapCustomerNumber`, invitation endpoint expects `customerNumber`
        const baseInvitationData = { ...invitationData, customerNumber: sapCustomerNumber }
        await updateInvitationMutation.mutateAsync({
          userId,
          data: {
            ...baseInvitationData,
            roles: (roleIds ?? []) as InvitationRolePayload[],
          } as unknown as UpdateInvitationFormValues,
        })
      } else {
        await editUserMutation.mutateAsync({ userId, data: data as unknown as Record<string, unknown> })

        await Promise.all([
          hasEmailChanged && data.email && data.countryId && data.language
            ? editUserEmailMutation.mutateAsync({
                userId,
                email: data.email,
                countryId: data.countryId,
                language: data.language,
              })
            : Promise.resolve(),
          hasSapChanged && data.sapCustomerNumber && data.email && data.countryId
            ? editCustomerSapMutation.mutateAsync({
                userId,
                email: data.email,
                sapCustomerId: data.sapCustomerNumber,
                countryId: data.countryId,
              })
            : Promise.resolve(),
          hasRolesChanged && data.roleIds
            ? assignRolesMutation.mutateAsync({ userId, roleIds: data.roleIds! })
            : Promise.resolve(),
        ])
      }

      onSuccess?.()
    } catch {
      // Errors are surfaced to the user via individual onError toast handlers.
    }
  }

  return {
    mutate,
    isPending,
  }
}
